import { useEffect, useState } from 'react';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { functionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';

type PermissionResponse = { permitted: boolean };

/**
 * The outcome of the JSM admin permission check, exposed to consumers as a single value so they can
 * switch on one status instead of juggling separate loading / error / result booleans.
 */
export const JsmAdminPermissionStatus = {
	/** The lookup has not resolved yet (also while the user identity is not hydrated). */
	Loading: 'loading',
	/** The user is a JSM/app admin. */
	Allowed: 'allowed',
	/** The lookup resolved and the user is not a JSM/app admin (also the gate-off result). */
	Denied: 'denied',
	/** The permission lookup failed. */
	Error: 'error',
} as const;

export type JsmAdminPermissionStatus =
	(typeof JsmAdminPermissionStatus)[keyof typeof JsmAdminPermissionStatus];

const toStatus = (permitted: boolean): JsmAdminPermissionStatus =>
	permitted ? JsmAdminPermissionStatus.Allowed : JsmAdminPermissionStatus.Denied;

const useJsmAdminPermissionEnabled = (): JsmAdminPermissionStatus => {
	const cloudId = useCloudId();
	const accountId = useAccountId();

	const [status, setStatus] = useState<JsmAdminPermissionStatus>(JsmAdminPermissionStatus.Loading);

	useEffect(() => {
		if (!accountId || !cloudId) {
			// Identity is not (yet) available — e.g. not hydrated on first render, or lost mid-session
			// on expiry. Reset to Loading so a previously resolved Allowed/Denied/Error result is not
			// left stale while there is no valid identity to check against.
			setStatus(JsmAdminPermissionStatus.Loading);
			return;
		}

		let cancelled = false;
		setStatus(JsmAdminPermissionStatus.Loading);

		// The effect callback itself must stay synchronous so it can return the cleanup function, so
		// the awaited request lives in this self-invoking async function.
		const fetchPermission = async () => {
			try {
				// The permissions service is queried via REST: the equivalent AGG GraphQL field
				// (`Query.permitted`) is deprecated, and this endpoint is the documented, non-deprecated
				// path (also used by `useIsOrgAdmin` and `useProjectPermission`).
				// TODO(ODYSSEY-1367): explore switching to a GraphQL permissions endpoint when available.
				const { permitted } = await performPostRequest<PermissionResponse>(
					'/gateway/api/permissions/permitted',
					{
						body: JSON.stringify({
							resourceId: `ari:cloud:jira-servicedesk::site/${cloudId}`,
							principalId: `ari:cloud:identity::user/${accountId}`,
							permissionId: 'manage',
						}),
					},
				);
				if (!cancelled) {
					setStatus(toStatus(permitted));
				}
			} catch (error: unknown) {
				if (!cancelled) {
					setStatus(JsmAdminPermissionStatus.Error);
				}
				// Reported outside the `cancelled` guard on purpose: a failed permission lookup is a
				// genuine backend/network signal worth capturing even when the component has unmounted
				// or the identity changed mid-flight. We skip only the state update for stale requests,
				// not the error report.
				fireErrorAnalytics({
					meta: {
						id: 'fetchJsmAdminPermission',
						packageName: 'jiraServicedeskJsmAdminPermission',
						teamName: 'jsm-odyssey',
					},
					error: error instanceof Error ? error : new Error('Failed to fetch JSM admin permission'),
					sendToPrivacyUnsafeSplunk: true,
				});
			}
		};

		fetchPermission();

		return () => {
			cancelled = true;
		};
	}, [accountId, cloudId]);

	return status;
};

/**
 * Resolves whether the current user is a JSM/app admin, fetched from the permissions service.
 * Returns a {@link JsmAdminPermissionStatus}; resolves to `Denied` when the gate is off.
 */
export const useJsmAdminPermission: () => JsmAdminPermissionStatus = functionWithCondition(
	() => fg('enable_jsm_admin'),
	useJsmAdminPermissionEnabled,
	(): JsmAdminPermissionStatus => JsmAdminPermissionStatus.Denied,
);
